import React, {useMemo, useRef, useState} from 'react';
import CLDropDownMenu, {
  CLDropDownMenuItemProps,
} from 'common/src/components/CLDropDownMenu/CLDropDownMenu';
import CLDropDownButton from 'common/src/components/CLDropDownButton/CLDropDownButton';
import CreateAssetFolderModal from '../CreateAssetFolderModal/CreateAssetFolderModal';
import {useUserRole} from 'common/src/modules/roles/hooks/useUserRole';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import DAMUploadInput, {DAMUploadInputRef} from '../DAMUploadInput/DAMUploadInput';
import CLTooltip from '../../../CLOverlayTrigger/overlays/CLTooltip/CLTooltip';
import _reduce from 'lodash/reduce';

import {CLSelectorOption} from 'common/src/components/Selectors/CLSelector/CLSelector.types';

export const ACCEPTED_TYPES: Record<string, string> = {
  png: 'image/png',
  apng: 'image/apng',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  webm: 'video/webm',
  mp4: 'video/mp4',
  mov: 'video/mov',
  svg: 'image/svg+xml',
  js: 'text/javascript',
  html: 'text/html',
  otf: 'font/otf',
  css: 'text/css',
  ttf: 'font/ttf',
  woff: 'font/woff',
  woff2: 'font/woff2',
  psd: 'image/vnd.adobe.photoshop',
  csv: 'text/csv',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  wav: 'audio/wav',
  mp3: 'audio/mp3',
  flac: 'audio/flac',
  ogg: 'audio/ogg',
  m4a: 'audio/m4a',
  srt: 'application/x-subrip',
};

interface DAMCreateProps {
  onCreate(files: File[]): void;
  onClose(): void;
  workingFolder: string;
  restrictedTypesOptions: CLSelectorOption[];
}

function DAMCreate({onCreate, onClose, workingFolder, restrictedTypesOptions}: DAMCreateProps) {
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const {hasRole: isUserWithCreativesWrite} = useUserRole(RoleTypeName.CreativesWrite);

  const acceptedTypes = useMemo(() => {
    if (!restrictedTypesOptions || !restrictedTypesOptions.length) return ACCEPTED_TYPES;

    return _reduce(
      restrictedTypesOptions,
      (acc, {value}) => {
        return {
          ...acc,
          [value]: ACCEPTED_TYPES[value],
        };
      },
      {},
    );
  }, [restrictedTypesOptions]);

  const fileInputRef = useRef<DAMUploadInputRef>();
  const menuItems: CLDropDownMenuItemProps[] = [
    {
      id: 'upload-file',
      content: 'Upload File',
      callBack() {
        setIsDropDownOpen(false);
        fileInputRef.current.uploadFiles();
      },
    },
    {
      id: 'add-folder',
      content: 'Create Folder',
      callBack() {
        setIsCreateFolderOpen(true);
        setIsDropDownOpen(false);
      },
    },
    {
      id: 'upload-folder',
      content: 'Upload Folder',
      callBack() {
        setIsDropDownOpen(false);
        fileInputRef.current.uploadFolder();
      },
    },
  ];

  const button = (
    <CLDropDownButton
      isOpen={isDropDownOpen}
      label="Add"
      disabled={!workingFolder}
      buttonType="primary"
      onClick={() => setIsDropDownOpen((open) => !open)}
    />
  );

  return (
    <>
      {!!workingFolder ? (
        <CLDropDownMenu
          menuItems={menuItems}
          isMenuVisible={isDropDownOpen}
          onClickOutside={() => setIsDropDownOpen(false)}
          placement="bottom-end"
        >
          {isUserWithCreativesWrite ? button : null}
        </CLDropDownMenu>
      ) : (
        <CLTooltip popper="Multiple folders have been selected">{button}</CLTooltip>
      )}
      {isCreateFolderOpen ? (
        <CreateAssetFolderModal
          onSubmit={async () => {
            await onClose();
            setIsCreateFolderOpen(false);
          }}
          onClose={() => setIsCreateFolderOpen(false)}
          workingFolder={workingFolder}
        />
      ) : null}
      <DAMUploadInput onUpload={onCreate} ref={fileInputRef} acceptedTypes={acceptedTypes} />
    </>
  );
}

export default DAMCreate;
